/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { cn } from '@/shared/ui/utils/tailwind';
import Button from '@/shared/ui/components/basic/Button';
import { Form, Text, TextInput } from '@/shared/ui';
import { Select } from '@/shared/ui/components/form/inputs';
import { buildSearchParams } from '@/shared/utils/partnerships-search-params';
import { usePartnershipsWizard, type SearchParams } from '../context/PartnershipsWizardContext';
import { BedroomSelector, CurrencyInput } from './FormFields';

const PROPERTY_TYPE_KEYS = [
  'apartamentos', 'casas', 'sobrados', 'coberturas', 'terrenos', 'comerciais',
] as const;

const TRANSACTION_KEYS = ['venda', 'aluguel'] as const;

const legendClass = cn(
  'text-xs font-semibold uppercase tracking-wide',
  'text-[var(--text-tertiary)] mb-[var(--space-3)]',
);

const fieldsetClass = cn(
  'border-none p-0 m-0',
  'flex flex-col gap-[var(--space-3)]',
);

/**
 * Wizard step that collects location, property, and price filters (seeded from
 * the reference property/client) and launches the partner portal search.
 *
 * @returns The filters step form.
 */
export function FiltersStep() {
  const { t } = useTranslation('features/partnerships');
  const { agency_id } = useParams<{ agency_id: string }>();
  const setStep = usePartnershipsWizard((s) => s.setStep);
  const seededFilters = usePartnershipsWizard((s) => s.seededFilters);
  const lastParams = usePartnershipsWizard((s) => s.lastParams);
  const setLastParams = usePartnershipsWizard((s) => s.setLastParams);
  const referenceLabel = usePartnershipsWizard((s) => s.referenceLabel);
  const source = usePartnershipsWizard((s) => s.source);
  const runSearch = usePartnershipsWizard((s) => s.runSearch);
  const loading = usePartnershipsWizard((s) => s.loading);

  const [city, setCity] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [propertyType, setPropertyType] = useState('apartamentos');
  const [transaction, setTransaction] = useState('venda');
  const [bedrooms, setBedrooms] = useState('');
  const [priceMin, setPriceMin] = useState<number | undefined>(undefined);
  const [priceMax, setPriceMax] = useState<number | undefined>(undefined);

  useEffect(() => {
    const filters = Object.keys(lastParams).length > 0 ? lastParams : seededFilters;
    if (filters.city) setCity(filters.city);
    if (filters.neighbourhood) setNeighborhood(filters.neighbourhood);
    if (filters.property_type) setPropertyType(filters.property_type);
    if (filters.transaction) setTransaction(filters.transaction);
    if (filters.bedrooms) setBedrooms(String(filters.bedrooms));
    if (filters.price_min) setPriceMin(Number(filters.price_min));
    if (filters.price_max) setPriceMax(Number(filters.price_max));
  }, [seededFilters, lastParams]);

  const propertyTypeOptions = useMemo(
    () => PROPERTY_TYPE_KEYS.map((key) => ({ value: key, label: t(`property_types.${key}`) })),
    [t],
  );
  const transactionOptions = useMemo(
    () => TRANSACTION_KEYS.map((key) => ({ value: key, label: t(`transactions.${key}`) })),
    [t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!city.trim() || !agency_id) return;
    const params: SearchParams = buildSearchParams(city, neighborhood, propertyType, transaction, bedrooms, priceMin, priceMax);
    setLastParams(params);
    setStep('results');
    await runSearch(params, agency_id);
  };

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      {source !== 'filter' && referenceLabel && (
        <div className={cn(
          'rounded-(--radius-sm) border border-[var(--border-subtle)]',
          'bg-[var(--surface-secondary)] px-[var(--space-3)] py-[var(--space-2)]',
          'text-sm',
        )}>
          <Text span fw={500}>{t('wizard.reference')}:</Text>{' '}
          {referenceLabel}
        </div>
      )}

      <Form
        onSubmit={handleSubmit}
        className={cn(
          'flex flex-col gap-[var(--space-5)]',
          'rounded-(--radius-lg) border border-[var(--border-subtle)]',
          'bg-[var(--surface-primary)] p-[var(--space-5)]',
        )}
      >
        <fieldset className={fieldsetClass}>
          <legend className={legendClass}>{t('form.section_location')}</legend>
          <div className="grid grid-cols-1 gap-[var(--space-4)] sm:grid-cols-2">
            <TextInput
              id="city"
              name="city"
              label={t('form.city_label')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={t('form.city_placeholder')}
              required
            />
            <TextInput
              id="neighborhood"
              name="neighborhood"
              label={t('form.neighborhood_label')}
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              placeholder={t('form.neighborhood_placeholder')}
            />
          </div>
        </fieldset>

        <fieldset className={fieldsetClass}>
          <legend className={legendClass}>{t('form.section_characteristics')}</legend>
          <div className="grid grid-cols-1 gap-[var(--space-4)] sm:grid-cols-3">
            <Select
              name="property_type"
              label={t('form.property_type_label')}
              data={propertyTypeOptions}
              value={propertyType}
              onChange={(v) => setPropertyType((Array.isArray(v) ? v[0] : v) || 'apartamentos')}
            />
            <Select
              name="transaction"
              label={t('form.transaction_label')}
              data={transactionOptions}
              value={transaction}
              onChange={(v) => setTransaction((Array.isArray(v) ? v[0] : v) || 'venda')}
            />
            <BedroomSelector
              label={t('form.bedrooms_label')}
              value={bedrooms}
              onChange={setBedrooms}
            />
          </div>
        </fieldset>

        <fieldset className={fieldsetClass}>
          <legend className={legendClass}>{t('form.section_price_range')}</legend>
          <div className="grid grid-cols-1 gap-[var(--space-4)] sm:grid-cols-2">
            <CurrencyInput id="price_min" label={t('form.price_min_label')} value={priceMin} onChange={setPriceMin} />
            <CurrencyInput id="price_max" label={t('form.price_max_label')} value={priceMax} onChange={setPriceMax} />
          </div>
        </fieldset>

        <div className="flex gap-[var(--space-3)]">
          <Button type="button" variant="outlined" onClick={() => setStep('source')}>
            {t('wizard.back')}
          </Button>
          <Button type="submit" variant="solid" disabled={loading || !city.trim()}>
            {loading ? t('form.searching') : t('form.submit')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
